import { getLocale, t } from "@travily/i18n";
import type Account from "@travily/types/models/Account";
import { Avatar } from "@ui/components/Avatar";
import { Link } from "@ui/primitives/Link";
import { localizedCountryName } from "@ui/utils/country-name";
import { cx } from "@ui/utils/cx";
import { localizedLanguageName } from "@ui/utils/language-name";

export interface ProgramLocation {
  name: string;
  country_code: string;
}

export interface SublineProps {
  account: Account;
  profileHref: string;
  location: ProgramLocation | null;
  languages: string[];
  className?: string;
}

function Dot({ className }: { className?: string }) {
  return <span aria-hidden className={cx("size-[3px] shrink-0 rounded-full bg-neutral-400", className)} />;
}

/**
 * The line(s) under the program name: the community that runs it (avatar + a link
 * to their profile), where it happens, and the languages it's taught in. On mobile
 * the community sits on its own row above a "location · languages" row (16px); on
 * desktop everything collapses to a single middot-separated 20px line.
 */
export function Subline({ account, profileHref, location, languages, className }: SublineProps) {
  const locationLabel = buildLocationLabel(location);
  const languagesLabel = formatLanguages(languages);

  const hasMeta = Boolean(locationLabel || languagesLabel);

  return (
    <div
      className={cx(
        // Structure steps to one line at md (with the desktop layout); the type
        // size only grows at xl, so through the midway range it reads as one
        // line at the mobile size.
        "flex flex-col text-base text-secondary tracking-[-0.16px]",
        "md:flex-row md:flex-wrap md:items-center md:gap-x-2",
        "xl:text-xl xl:tracking-normal",
        className,
      )}
    >
      <span className="flex items-center gap-2">
        <Avatar.Root color="green" shape="circle" size="24">
          {account.avatar && <Avatar.Image image={account.avatar} />}
          {account.initial && <Avatar.Fallback>{account.initial}</Avatar.Fallback>}
        </Avatar.Root>
        <Link href={profileHref} prefetch="hover" className="font-semibold hover:underline xl:font-normal">
          {account.display_name ?? account.handle}
        </Link>
        {/* Trailing dot (one-line layout only) — kept on the community so it never
            lands at the start of a wrapped line. */}
        {hasMeta && <Dot className="hidden md:block" />}
      </span>

      {hasMeta && (
        <span className="flex items-center gap-2">
          {locationLabel && <span>{locationLabel}</span>}
          {locationLabel && languagesLabel && <Dot />}
          {languagesLabel && (
            <span>{t("components.views.programs.show.languages", { languages: languagesLabel })}</span>
          )}
        </span>
      )}
    </div>
  );
}

/**
 * Place name plus country, but only append the country when the name doesn't
 * already carry it (Mapbox names range from bare "Innsbruck" to "Goa, India").
 * Also feeds the desktop `SectionJumpBar` subtitle.
 */
export function buildLocationLabel(location: ProgramLocation | null): string | null {
  if (!location) return null;
  const country = localizedCountryName(location.country_code);
  if (!country || location.name.includes(country)) return location.name;
  return `${location.name} ${country}`;
}

function formatLanguages(codes: string[]): string | null {
  if (codes.length === 0) return null;
  const names = codes.map((code) => localizedLanguageName(code));
  return new Intl.ListFormat(getLocale(), { style: "short", type: "conjunction" }).format(names);
}
